// src/utils/tenantConfig.js
import { 
  obtenerConfigEmpresa, 
  guardarConfigEmpresa, 
  eliminarConfigEmpresa 
} from '@/api/empresa.js';

import { getCurrentApiUrl } from '@/api/index.js';

const STORAGE_KEY = 'tenantConfig';

/**
 * Obtiene la URL base del backend (funciona en dev y en producción)
 */
const getBackendUrl = () => {
  try {
    return getCurrentApiUrl();
  } catch (error) {
    console.warn('Error obteniendo URL del backend, usando fallback:', error);
    return process.env.NODE_ENV === 'production'
      ? 'https://api.sistemas-jj.com'
      : 'http://localhost:5090';
  }
};

export const getAssetUrl = (assetPath) => {
  if (!assetPath) return null;
  if (assetPath.startsWith('data:')) {
    return assetPath;
  }
  if (/^https?:\/\//i.test(assetPath)) {
    try {
      const parsedUrl = new URL(assetPath);
      if (parsedUrl.pathname.startsWith('/tenants/')) {
        return `${getBackendUrl()}${parsedUrl.pathname}${parsedUrl.search}`;
      }
    } catch {
      return assetPath;
    }
    return assetPath;
  }
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${getBackendUrl()}${normalizedPath}`;
};

/**
 * Carga la config del tenant desde el backend y la cachea
 */
export const cargarConfigTenant = async (tenantId) => {
  try {
    const res = await obtenerConfigEmpresa(tenantId);
    if (res.success && res.data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data));
      // 👈 Guardar también el tenantId para saber cuál es la empresa actual
      localStorage.setItem('tenantId', tenantId);
      aplicarConfigTenant(res.data);
      return res.data;
    }
    return null;
  } catch (error) {
    console.error('Error cargando config del tenant:', error);
    return null;
  }
};

/**
 * Guarda la config del tenant en el backend Y la aplica al localStorage/DOM.
 * 
 * ⚠️ USAR SOLO cuando el cambio corresponde a la empresa ACTUAL del usuario.
 * ❌ NO usar al crear una empresa nueva desde el panel de super admin.
 * Para ese caso usar `guardarConfigTenantSinAplicar`.
 */
export const guardarConfigTenant = async (tenantId, config, logoBase64 = null, faviconBase64 = null) => {
  try {
    const res = await guardarConfigEmpresa(tenantId, config, logoBase64, faviconBase64);
    if (res.success) {
      const configConLogo = {
        ...config,
        idEmpresa: tenantId,
        logoBase64: logoBase64 || config.logoBase64 || null,
        faviconBase64: faviconBase64 || config.faviconBase64 || null
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(configConLogo));
      aplicarConfigTenant(configConLogo);
      window.dispatchEvent(new CustomEvent('tenantConfigUpdated'));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error guardando config del tenant:', error);
    return false;
  }
};

/**
 * 👈 Guarda SOLO en el backend, sin tocar el localStorage ni el DOM.
 * 
 * ✅ USAR cuando creas una empresa nueva estando logueado como super admin,
 * para no sobreescribir la config de tu propia empresa.
 */
export const guardarConfigTenantSinAplicar = async (tenantId, config, logoBase64 = null, faviconBase64 = null) => {
  try {
    const res = await guardarConfigEmpresa(tenantId, config, logoBase64, faviconBase64);
    return res.success;
  } catch (error) {
    console.error('Error guardando config del tenant (sin aplicar):', error);
    return false;
  }
};

/**
 * Elimina la config del tenant
 */
export const eliminarConfigTenant = async (tenantId) => {
  try {
    const res = await eliminarConfigEmpresa(tenantId);
    if (res.success) {
      const configActual = obtenerConfigTenant();
      if (configActual?.idEmpresa === tenantId) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('tenantId');
        window.dispatchEvent(new CustomEvent('tenantConfigUpdated'));
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error eliminando config del tenant:', error);
    return false;
  }
};

/**
 * Lee la config del tenant de la caché local
 */
export const obtenerConfigTenant = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.error('Error obteniendo config de caché:', error);
    return null;
  }
};

/**
 * Limpia la config (al cerrar sesión)
 */
export const limpiarConfigTenant = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('tenantId');
};

/**
 * Aplica la config al DOM (título + favicon).
 * 
 * Para el favicon:
 * - Elimina los <link rel="icon"> anteriores para evitar caché del navegador.
 * - Crea un <link> NUEVO cada vez (rompe la caché interna del navegador).
 * - Valida que el Base64 no sea excesivamente grande (>1.5MB suele fallar).
 * - Añade ?v=timestamp a URLs (no a Base64) para forzar recarga.
 */
export const aplicarConfigTenant = (config = null) => {
  if (!config) config = obtenerConfigTenant();
  if (!config) return;

  // ============================================================
  // 1. TÍTULO
  // ============================================================
  const nuevoTitulo = config.nombreComercial || config.nombreEmpresa;
  if (nuevoTitulo) {
    document.title = nuevoTitulo;

    const titleEl = document.getElementById('app-title');
    if (titleEl) {
      titleEl.textContent = nuevoTitulo;
    }
  }

  // ============================================================
  // 2. FAVICON
  // ============================================================
  // Prioridad: faviconBase64 > logoBase64 > logoUrl (del backend)
  let faviconData = config.faviconBase64 || config.logoBase64 || null;

  // Si no hay Base64 pero sí logoUrl del backend, construir URL completa
  if (!faviconData && config.logoUrl) {
    try {
      faviconData = getAssetUrl(config.logoUrl);
    } catch (err) {
      console.warn('⚠️ No se pudo construir URL de logo:', err);
    }
  }

  // Si no hay nada, no tocar el favicon
  if (!faviconData) {
    console.log('ℹ️ No hay favicon configurado para el tenant');
    return;
  }

  // Validar tamaño (algunos navegadores ignoran Base64 > ~1.5MB)
  if (faviconData.startsWith('data:') && faviconData.length > 1_500_000) {
    console.warn('⚠️ Favicon Base64 demasiado grande, intentando con logo...');
    if (config.logoBase64 && config.logoBase64.length <= 1_500_000) {
      faviconData = config.logoBase64;
    }
  }

  try {
    // 👈 CLAVE: eliminar TODOS los <link rel="icon"> anteriores
    const faviconsAntiguos = document.querySelectorAll("link[rel*='icon']");
    faviconsAntiguos.forEach((el) => {
      if (!el.rel.includes('apple')) {
        el.parentNode.removeChild(el);
      }
    });

    // 👈 Crear uno NUEVO (rompe la caché del navegador)
    const nuevoFavicon = document.createElement('link');
    nuevoFavicon.id = 'app-favicon';
    nuevoFavicon.rel = 'icon';
    nuevoFavicon.type = 'image/png';

    // Si es Base64, no añadir query string (rompería el data URI)
    // Si es URL, añadir timestamp para evitar caché
    if (faviconData.startsWith('data:')) {
      nuevoFavicon.href = faviconData;
    } else {
      const sep = faviconData.includes('?') ? '&' : '?';
      nuevoFavicon.href = `${faviconData}${sep}v=${Date.now()}`;
    }

    document.head.appendChild(nuevoFavicon);

    console.log('✅ Favicon aplicado:', {
      tenant: config.idEmpresa || config.nombreEmpresa,
      tipo: faviconData.startsWith('data:') ? 'base64' : 'url',
      tamaño: faviconData.length,
    });
  } catch (err) {
    console.error('❌ Error aplicando favicon:', err);
  }
};

/**
 * Convierte un archivo a Base64
 */
export const archivoABase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No se proporcionó archivo'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Obtiene el logo del tenant usando getCurrentApiUrl()
 */
export const obtenerLogo = () => {
  const config = obtenerConfigTenant();

  if (config?.logoBase64) {
    return config.logoBase64;
  }

  if (config?.logoUrl) {
    return getAssetUrl(config.logoUrl);
  }

  return '/jj.png';
};

/**
 * Obtiene los datos de la EMPRESA CLIENTE (tenant) para tickets/facturas.
 * 
 * ⚠️ NO confundir con los datos del desarrollador (SYSTEM JJ).
 * Este método devuelve los datos de la empresa que COMPRÓ el sistema.
 * 
 * Si no hay config cargada, devuelve valores genéricos vacíos
 * (NUNCA los datos de SYSTEM JJ, porque esto lo ven los CLIENTES).
 */
export const obtenerDatosEmpresa = () => {
  const config = obtenerConfigTenant();
  
  if (config) {
    let logoUrl = '/jj.png';
    if (config.logoBase64) {
      logoUrl = config.logoBase64;
    } else if (config.logoUrl) {
      logoUrl = getAssetUrl(config.logoUrl);
    }

    return {
      nombre: config.nombreEmpresa || '',
      nombreComercial: config.nombreComercial || config.nombreEmpresa || '',
      rif: config.rif || '',
      direccion: config.direccion || '',
      ciudad: config.ciudad || '',
      telefono1: config.telefono1 || '',
      telefono2: config.telefono2 || '',
      email: config.email || '',
      slogan: config.slogan || '',
      whatsapp: config.whatsapp || '',
      instagram: config.instagram || '',
      facebook: config.facebook || '',
      tiktok: config.tiktok || '',
      logoBase64: logoUrl,
      condiciones: config.condiciones || []
    };
  }

  // ⚠️ Fallback genérico cuando NO hay config de tenant cargada.
  // NO poner datos de SYSTEM JJ aquí porque esto lo usan los CLIENTES.
  console.warn('⚠️ No hay configuración de tenant cargada. Los datos de empresa estarán vacíos.');
  
  return {
    nombre: '',
    nombreComercial: '',
    rif: '',
    direccion: '',
    ciudad: '',
    telefono1: '',
    telefono2: '',
    email: '',
    slogan: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    logoBase64: '/jj.png',
    condiciones: []
  };
};

export default {
  cargarConfigTenant,
  guardarConfigTenant,
  guardarConfigTenantSinAplicar,
  eliminarConfigTenant,
  obtenerConfigTenant,
  limpiarConfigTenant,
  aplicarConfigTenant,
  archivoABase64,
  obtenerLogo,
  obtenerDatosEmpresa
};